<template>
    <div class="container d-flex flex-column">
      <ValidationObserver ref="observer"
                          tag="form"
                          class="row d-flex justify-content-center mx-2 mt-5"
                          @submit.prevent="submit">
          <div class="col-xl-5 col-lg-7 col-md-9 bg-white shadow py-5 px-3 px-sm-5 rounded">
            <template v-if="isTokenValidating">
              <div class="text-center py-4">
                <div class="spinner-border text-primary" role="status">
                  <span class="sr-only">Notiek pārbaude...</span>
                </div>
                <p class="mt-2 text-secondary">Pārbauda saiti...</p>
              </div>
            </template>
            <template v-else-if="tokenInvalid">
              <div class="alert alert-danger mb-4 text-left">
                Šī saite vairs nav derīga vai ir nepareiza.
                  <div class="mt-3">
                    <router-link :to="{ name: 'login' }" class="ds-btn">Uz sākumlapu</router-link>
                  </div>
              </div>
            </template>
            <template v-else>
              <h4 class="mb-4 text-secondary">Jaunas paroles iestatīšana</h4>


              <div v-if="successMessage" class="alert alert-success mb-4 text-left">
                  {{ successMessage }}
                  <div class="mt-3">
                      <router-link :to="{ name: 'login' }" class="ds-btn">Doties uz pieslēgšanos</router-link>
                  </div>
              </div>

              <template v-else-if="!tokenInvalid">
                  <ValidationProvider tag="div"
                                      class="mb-4"
                                      vid="password"
                                      :bails="false"
                                      mode="eager"
                                      :rules="{
                                          min: 9,
                                          required: true,
                                          regex: passwordRegex,
                                      }"
                                      :customMessages="{
                                          min: 'Parolei jābut vismaz 9 simboliem.',
                                          required: 'Parole nevar būt tukša.',
                                          regex: 'Parolei jāsastāv no 1 lielā burta, 1 mazā burta, cipara, īpašā simbola (!@#$%?&^*).',
                                      }"
                                      v-slot="{errors}">
                      <label for="password" class="d-block text-secondary text-left mb-0">Jaunā parole</label>
                      <input type="password" id="password" class="w-100 py-1 ds-input" v-model="password">
                      <ul class="pl-3 mt-1">
                          <li class="text-danger text-left small" v-for="error in errors" :key="error">{{ error }}</li>
                      </ul>
                  </ValidationProvider>

                  <ValidationProvider tag="div"
                                      class="mb-4"
                                      mode="eager"
                                      vid="confirmPassword"
                                      :rules="{
                                          required: true,
                                          is: password,
                                      }"
                                      :customMessages="{required: 'Apstiprini jauno paroli.', is: 'Nesakrīt ar jauno paroli.'}"
                                      v-slot="{errors}">
                      <label for="confirmPassword" class="d-block text-secondary text-left mb-0">
                          Jaunās paroles apstiprinājums
                      </label>
                      <input type="password" id="confirmPassword" class="w-100 py-1 ds-input" v-model="confirmPassword">
                      <span class="text-danger d-block text-left small mt-1">{{ errors[0] }}</span>
                  </ValidationProvider>

                  <CustomButton class="w-100" btn-text="Atjaunot paroli" :is-loading="isLoading"/>
              </template>

              <div v-if="!successMessage && !tokenInvalid" class="mt-4 text-center">
                  <router-link :to="{ name: 'login' }" class="text-secondary small">Atpakaļ uz pieslēgšanos</router-link>
              </div>
              <div v-if="isRateLimited" class="text-danger d-block mt-2">Pārāk daudz mēģinājumu, lūdzu, mēģiniet vēlreiz vēlāk.</div>
            </template>
          </div>
      </ValidationObserver>
    </div>
</template>

<script>
    import {ValidationObserver, ValidationProvider, extend} from 'vee-validate';
    import {required, regex, min, is} from 'vee-validate/dist/rules';
    import axios from 'axios';

    import CustomButton from '@/components/global/CustomButton';
    import {uiPasswordRegex} from '../../utils/password-generator';

    export default {
        name: 'ResetPassword',
        components: {
            CustomButton,
            ValidationProvider,
            ValidationObserver,
        },
        created() {
            extend('required', required);
            extend('regex', regex);
            extend('min', min);
            extend('is', is);
        },
        data: () => ({
            password: '',
            confirmPassword: '',
            isLoading: false,
            isTokenValidating: false,
            successMessage: '',
            tokenInvalid: false,
            passwordRegex: uiPasswordRegex,
            isRateLimited: false,
        }),
        async mounted() {
            const token = this.$route.query.token;
            if (!token) {
                this.$router.push({name: 'login'});
                return;
            }

            this.isTokenValidating = true;
            try {
                const {data} = await axios.get(`/api/auth/validate-token/${token}`);
                if (!data.success) {
                    this.tokenInvalid = true;
                    setTimeout(() => {
                        this.$router.push({name: 'login'});
                    }, 5000);
                }
            } catch (error) {
                this.tokenInvalid = true;
                setTimeout(() => {
                    this.$router.push({name: 'login'});
                }, 5000);
            } finally {
                this.isTokenValidating = false;
            }
        },
        methods: {
            async submit() {
                this.isRateLimited = false;
                const isValid = await this.$refs.observer.validate();

                if (!isValid) {
                    return;
                }

                this.isLoading = true;
                try {
                    const {data} = await axios.post('/api/auth/reset-password', {
                        token: this.$route.query.token,
                        password: this.password,
                    });

                    if (data.success) {
                        this.successMessage = data.message;
                    }
                } catch (error) {
                    const errorData = error.response && error.response.data && error.response.data.error
                        ? error.response.data.error
                        : null;

                    if (errorData && errorData.field) {
                        this.$refs.observer.setErrors({[errorData.field]: [errorData.message]});
                    } else if (errorData && errorData.isRateLimit) {
                        this.isRateLimited = true;
                    }
                } finally {
                    this.isLoading = false;
                }
            },
        },
    }
</script>
